import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLoading,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import TestEnvBanner from "./TestEnvBanner";
import { chevronBackOutline } from "ionicons/icons";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { getDocumentsForItem } from "../api/ItemSpecDocuments";
import { WoDocument } from "../models/workorders/Documents";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { useDocumentManager } from "../hooks/useDocumentManager";
import { decodeParam } from "../util/ApiHelper";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import DocumentListDisplay from "../components/DocumentListDisplay";

const DocumentsForInventoryItem: React.FC = () => {
  const [showLoading, setShowLoading] = useState(false);
  const { itemId } = useParams<{ itemId: string }>();
  let displayItemId = decodeParam(itemId);

  const [documents, setDocuments] = useState<WoDocument[]>([]);
  const [errorData, setErrorData] = useState("");
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { viewDocument } = useDocumentManager();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    setShowLoading(true);
    getDocumentsForItem({ itemId: displayItemId })
      .then((response) => {
        if (response.isError) {
          setErrorData(response.data?.toString());
          setDocuments([]);
        } else if (response.data) {
          if (response.data && response.data.length > 0) {
            setDocuments(response.data);
          } else {
            setDocuments([]);
          }
        }
      })
      .finally(() => {
        setShowLoading(false);
      });
  }, [displayItemId]);

  const showAlert = useCallback(
    (message: string) => {
      presentAlert({
        message: message,
        buttons: ["OK"],
      });
    },
    [presentAlert]
  );

  const onDocumentClick = useCallback(
    async (document: WoDocument) => {
      if (document && !!document.DownloadURL) {
        setShowLoading(true);

        await viewDocument({ WoNumber: displayItemId, Document: document });

        setShowLoading(false);
      } else {
        showAlert(
          translatedMessages["DocProviderMissing"]?.MessageText ||
            "Cannot Open this document as document source information is missing"
        );
      }
    },
    [displayItemId, showAlert, translatedMessages, viewDocument]
  );

  useEffect(() => {
    if (documents.length === 1) onDocumentClick(documents[0]);
  }, [documents.length]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton routerDirection="back" routerLink="/Inventory">
              <IonIcon slot="icon-only" icon={chevronBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>
            {translations["lbl_mob_inventoryitem_docs"] || "Documents"}
          </IonTitle>
        </IonToolbar>
        <TestEnvBanner />
      </IonHeader>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <IonContent className="ion-padding">
        <DocumentListDisplay
          documents={documents}
          onDocumentClick={onDocumentClick}
          translations={translations}
          translatedMessages={translatedMessages}
          showLoading={showLoading}
          errorData={errorData}
        />
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  DocumentsForInventoryItem,
  "DocumentsForInventoryItem"
);
